'use client';

import type { ClassValue } from 'clsx';

import { clsxm } from '@vs-shop/utils';

import {
  FavouritesPicker,
  FavouritesPickerEmpty,
  FavouritesPickerEmptyText,
  FavouritesPickerOption,
  FavouritesPickerOptionLabel,
  FavouritesPickerOptionName,
  FavouritesPickerOptions,
  FavouritesPickerPanel,
  FavouritesPickerPending,
  FavouritesPickerTitle,
} from '@vs-shop/commerce-ui/favouritesPicker';
import { Checkbox } from '@vs-shop/ui/checkbox';
import { Ban } from 'lucide-react';
import { useIntl } from 'react-intl';
import { MoonLoader } from 'react-spinners';

import type { ProductFavouriteListWithProductCodes } from '../../../../effect/schema/openapi/backend';

type Props = {
  lists: readonly ProductFavouriteListWithProductCodes[];
  checkedListIds: ReadonlySet<string>;
  onCheckedChangeAction: (listId: string, checked: boolean) => void;
  isLoading?: boolean;
  className?: ClassValue;
};

export default function ProductFavouritesModalLists({
  lists,
  checkedListIds,
  onCheckedChangeAction,
  isLoading = false,
  className,
}: Props) {
  const intl = useIntl();

  const hasLists = lists.length > 0;

  return (
    <FavouritesPicker className={clsxm(className)}>
      <FavouritesPickerTitle>
        {intl.formatMessage({
          id: 'productFavouritesModal.title.selectList',
          defaultMessage: 'Vyberte seznam',
        })}
      </FavouritesPickerTitle>

      <FavouritesPickerPanel>
        {isLoading ? (
          // ===== LOADING
          <FavouritesPickerPending>
            <MoonLoader />
          </FavouritesPickerPending>
        ) : hasLists ? (
          // ===== LISTS
          <FavouritesPickerOptions>
            {lists.map((list) => {
              const listId = list.id ?? '';

              return (
                <FavouritesPickerOption key={listId}>
                  <FavouritesPickerOptionLabel htmlFor={listId}>
                    <Checkbox
                      id={listId}
                      className="size-5"
                      checked={checkedListIds.has(listId)}
                      onCheckedChange={(checked) => {
                        onCheckedChangeAction(listId, checked);
                      }}
                    />
                    <FavouritesPickerOptionName>{list.name}</FavouritesPickerOptionName>
                  </FavouritesPickerOptionLabel>
                </FavouritesPickerOption>
              );
            })}
          </FavouritesPickerOptions>
        ) : (
          // ===== EMPTY
          <FavouritesPickerEmpty>
            <Ban className="size-4 shrink-0 stroke-1" />
            <FavouritesPickerEmptyText>
              {intl.formatMessage({
                id: 'productFavouritesModal.lists.empty',
                defaultMessage:
                  'Zatím nemáte vytvořený žádný seznam oblíbených produktů. Vytvořte nový seznam a poté přidejte položku do oblíbených.',
              })}
            </FavouritesPickerEmptyText>
          </FavouritesPickerEmpty>
        )}
      </FavouritesPickerPanel>
    </FavouritesPicker>
  );
}
